import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import Swal from 'sweetalert2';
import PageHeader from '../../components/PageHeader';

const API = process.env.REACT_APP_API_URL;
const LMS = process.env.REACT_APP_API_LMS;
const LMS_TOKEN = process.env.REACT_APP_API_TOKEN;

const BATCH_TYPES = [
  { value: 'regular',   label: 'Regular',   description: 'Weekdays' },
  { value: 'weekend',   label: 'Weekend',   description: 'Saturday and Sunday' },
];

const DAYS_OF_WEEK = [
  { value: 'Mon',    label: 'Monday' },
  { value: 'Tue',   label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu',  label: 'Thursday' },
  { value: 'Fri',    label: 'Friday' },
  { value: 'Sat',  label: 'Saturday' },
  { value: 'Sun',    label: 'Sunday' },
];

// Custom styles for react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#0d6efd' : base.borderColor,
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : base.boxShadow,
    '&:hover': {
      borderColor: '#0d6efd'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : base.backgroundColor,
    color: state.isSelected ? '#fff' : base.color,
    '&:active': {
      backgroundColor: '#0d6efd'
    }
  })
};

const InstituteAddBatch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [programs, setPrograms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isEditDataLoaded, setIsEditDataLoaded] = useState(false); // Track if edit data is loaded
  const [didUserChangeBatchType, setDidUserChangeBatchType] = useState(false);

  const Institute_token = sessionStorage.getItem('token');
  const Institute_id = sessionStorage.getItem('instituteId');

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      program: null,
      teacher: null,
      batchName: '',
      startDate: '',
      endDate: '',
      batchType: { value: 'regular', label: 'Regular — Weekdays' },
      days: [],
      time: '',
      status: 'active',
      capacity: '',
      description: '',
    },
  });

  const watchBatchType = watch('batchType');
  const watchProgram   = watch('program');
  const watchTeacher   = watch('teacher'); 
  const watchBatchName = watch('batchName');
  const watchStart     = watch('startDate');
  const watchEnd       = watch('endDate');
  const watchTime      = watch('time');
  const watchDays      = watch('days');
  const watchCapacity  = watch('capacity');
  const watchStatus    = watch('status');

  // Fetch Programs
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await fetch(`${LMS}/api/institutes/${Institute_id}/programs`, {
          method: 'GET',
          headers: {
            'Authorization': LMS_TOKEN,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch programs');
        
        const data = await response.json();
        setPrograms(data.data || data || []);
      } catch (error) {
        console.error('Error fetching programs:', error);
        Swal.fire({ icon: 'error', title: 'Failed to load programs' });
      }
    };

    fetchPrograms();
  }, [Institute_id]);

  // Fetch Teachers
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await fetch(`${API}/api/teacherbyinstitute?institute_id=${Institute_id}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Institute_token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch teachers');
        
        const data = await response.json();
        setTeachers(data.data || data || []);
      } catch (error) {
        console.error('Error fetching teachers:', error);
        Swal.fire({ icon: 'error', title: 'Failed to load teachers' });
      } finally {
        setDataLoading(false);
      }
    };

    fetchTeachers();
  }, [Institute_id, Institute_token]);

  // Fetch Batch Details for Edit
  useEffect(() => {
    if (!isEdit || !id || programs.length === 0 || teachers.length === 0) return;

    const fetchBatchDetails = async () => {
      setDataLoading(true);
      try {
        const response = await fetch(`${LMS}/api/batch/${id}`, {
          method: 'GET',
          headers: {
            'Authorization': LMS_TOKEN,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch batch details');
        
        const data = await response.json();
        const batch = data.data || data;

        // Populate form with batch data - convert to react-select format
        const selectedProgram = programs.find(p => p.id == batch.program_id);
        const selectedTeacher = teachers.find(t => t.id == batch.teacher_id);
        const selectedBatchType = BATCH_TYPES.find(bt => bt.value === batch.batch_type);

        if (selectedProgram) {
          setValue('program', { 
            value: selectedProgram.id, 
            label: selectedProgram.name || selectedProgram.program_title || selectedProgram.program_name
          });
        }
        
        if (selectedTeacher) {
          setValue('teacher', { 
            value: selectedTeacher.id, 
            label: `${selectedTeacher.first_name} ${selectedTeacher.last_name}` || selectedTeacher.name || selectedTeacher.teacher_name
          });
        }

        setValue('batchName', batch.batch_name);
        setValue('startDate', batch.start_date);
        setValue('endDate', batch.end_date);
        
        if (selectedBatchType) {
          setValue('batchType', {
            value: selectedBatchType.value,
            label: `${selectedBatchType.label} — ${selectedBatchType.description}`
          });
        }
        
        // Parse days from API - handle both string and array formats
        const apiDays = batch.days ? (typeof batch.days === 'string' ? batch.days.split(',').map(d => d.trim()) : batch.days) : [];
        setValue('days', apiDays);
        
        setValue('time', batch.time?.substring(0, 5) || batch.time); // Handle HH:MM:SS format
        setValue('status', batch.status);
        setValue('capacity', batch.capacity);
        setValue('description', batch.description || '');
        
        // Mark that edit data has been loaded
        setIsEditDataLoaded(true);
      } catch (error) {
        console.error('Error fetching batch details:', error);
        Swal.fire({ icon: 'error', title: 'Failed to load batch details' });
      } finally {
        setDataLoading(false);
      }
    };

    fetchBatchDetails();
  }, [isEdit, id, setValue, programs, teachers]);

  // Auto-select days based on batch type
  useEffect(() => {
    if (!watchBatchType) return;

    let selected = [];
    switch (watchBatchType.value) {
      case 'regular':
        selected = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        break;
      case 'weekend':
        selected = ['Sat', 'Sun'];
        break;
      default:
        selected = [];
    }

    if (!isEdit) {
      setValue('days', selected);
    } else if (didUserChangeBatchType) {
      setValue('days', selected);
    }
  }, [watchBatchType, setValue, isEdit, didUserChangeBatchType]);


  // Convert programs and teachers to react-select format
  const programOptions = programs.map(p => ({
    value: p.id,
    label: p.name || p.program_title || p.program_name
  }));

  const teacherOptions = teachers.map(t => ({
    value: t.id,
    label: `${t.first_name} ${t.last_name}` || t.name || t.teacher_name
  }));

  const batchTypeOptions = BATCH_TYPES.map(bt => ({
    value: bt.value,
    label: `${bt.label} — ${bt.description}`
  }));

  const onSubmit = async (data) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        program_id: parseInt(data.program?.value),
        institute_id: parseInt(Institute_id),
        teacher_id: parseInt(data.teacher?.value),
        batch_name: data.batchName,
        start_date: data.startDate,
        end_date: data.endDate,
        batch_type: data.batchType?.value,
        days: Array.isArray(data.days) ? data.days : data.days.split(','),
        time: data.time + ':00', // Ensure time format is HH:MM:SS
        status: data.status,
        capacity: parseInt(data.capacity),
        description: data.description || '',
      };

      const url = isEdit ? `${LMS}/api/batch/${id}` : `${LMS}/api/batch`;
      const method = isEdit ? 'PUT' : 'POST';

      // For edit, use program_ids array instead of program_id
      if (isEdit) {
        payload.program_ids = payload.program_id;
        delete payload.program_id;
        // Days should be comma-separated string for PUT
        payload.days = Array.isArray(payload.days) ? payload.days.join(',') : payload.days;
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': LMS_TOKEN,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save batch');
      }

      const result = await response.json();
      console.log('Batch saved:', result);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Batch "${data.batchName}" ${isEdit ? 'updated' : 'created'} successfully!`,
        timer: 1200,
        showConfirmButton: false
      }).then(() => navigate('/institute-batch'));
    } catch (error) {
      console.error('Error saving batch:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save batch. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    reset();
    setMessage({ type: '', text: '' });
    setIsEditDataLoaded(false);
  };

  const todayStr = new Date().toISOString().split('T')[0];

  if (dataLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Batch' : 'Add Batch'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/institute-dashboard' },
          { label: 'Batches', path: '/institute-batch' },
          { label: isEdit ? 'Edit' : 'Add', active: true },
        ]}
      />

      {message.text && (
        <Alert 
          variant={message.type} 
          dismissible 
          onClose={() => setMessage({ type: '', text: '' })} 
          className={message.type === 'success' ? 'text-white' : ''}
        >
          {message.text}
        </Alert>
      )}

      <Card className="skyblueborder">
        <Card.Header className="skyblue-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <h5 className="mb-0 text-primary">{isEdit ? 'Update Batch Details' : 'Enter New Batch Details'}</h5>
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            onClick={() => navigate('/institute-batch')}
          >
            ← Back to Batches List
          </Button>
        </Card.Header>
        <Card.Body>
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Program <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="program"
                    control={control}
                    rules={{ required: 'Program is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={programOptions}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.program ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.program ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                        placeholder="Choose program..."
                        isClearable
                        className={errors.program ? 'is-invalid' : ''}
                      />
                    )}
                  />
                  {errors.program && (
                    <div className="invalid-feedback d-block">{errors.program?.message}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Teacher <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="teacher"
                    control={control}
                    rules={{ required: 'Teacher is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={teacherOptions}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.teacher ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.teacher ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                        placeholder="Choose teacher..."
                        isClearable
                        className={errors.teacher ? 'is-invalid' : ''}
                      />
                    )}
                  />
                  {errors.teacher && (
                    <div className="invalid-feedback d-block">{errors.teacher?.message}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Batch Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    {...register('batchName', {
                      required: 'Batch name is required',
                      minLength: { value: 3, message: 'Min 3 characters' },
                    })}
                    isInvalid={errors.batchName}
                    placeholder="e.g. Morning Batch A1"
                  />
                  <Form.Control.Feedback type="invalid">{errors.batchName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    {...register('startDate', { required: 'Start date is required' })}
                    isInvalid={errors.startDate}
                    min={todayStr}
                    onFocus={(e) => e.target.showPicker?.()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.startDate?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    {...register('endDate', {
                      required: 'End date is required',
                      validate: (val) =>
                        watchStart && new Date(val) > new Date(watchStart) ? true : 'Must be after start date',
                    })}
                    isInvalid={errors.endDate}
                    min={watchStart || todayStr}
                    onFocus={(e) => e.target.showPicker?.()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.endDate?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="time"
                    {...register('time', { required: 'Time is required' })}
                    isInvalid={errors.time}
                    onFocus={(e) => e.target.showPicker?.()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.time?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Capacity <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    {...register('capacity', {
                      required: 'Capacity is required',
                      min: { value: 1, message: 'Min 1' },
                      max: { value: 500, message: 'Max 500' },
                    })}
                    isInvalid={errors.capacity}
                  />
                  <Form.Control.Feedback type="invalid">{errors.capacity?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Batch Type</Form.Label>
                  <Controller
                    name="batchType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={batchTypeOptions}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.batchType ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.batchType ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                        placeholder="Choose batch type..."
                        onChange={(val) => {
                          setDidUserChangeBatchType(true);
                          field.onChange(val);
                        }}
                      />
                    )}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-4 align-items-center" style={{ height: '38px' }}>
                    <Form.Check
                      type="radio"
                      id="status-active"
                      label="Active"
                      value="active"
                      {...register('status')}
                      checked={watchStatus === 'active'}
                    />
                    <Form.Check
                      type="radio"
                      id="status-inactive"
                      label="Inactive"
                      value="inactive"
                      {...register('status')}
                      checked={watchStatus === 'inactive'}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Days */}
            <Row className="mb-3">
              <Col>
                <Form.Label>Days <span className="text-danger">*</span></Form.Label>
                <div className="border rounded p-3 bg-light">
                  <Row>
                    {DAYS_OF_WEEK.map((day) => (
                      <Col md={3} sm={6} key={day.value}>
                        <Form.Check
                          type="checkbox"
                          id={day.value}
                          label={day.label}
                          checked={watchDays.includes(day.value)}
                          onChange={() => {
                            const curr = [...watchDays];
                            if (curr.includes(day.value)) {
                              setValue(
                                'days',
                                curr.filter((d) => d !== day.value)
                              );
                            } else {
                              setValue('days', [...curr, day.value]);
                            }
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
                {errors.days && <small className="text-danger">{errors.days.message}</small>}
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    {...register('description', {
                      maxLength: { value: 500, message: 'Max 500 characters' }
                    })} 
                    isInvalid={errors.description}
                  />
                  <Form.Text muted>{watch('description')?.length || 0}/500</Form.Text>
                  <Form.Control.Feedback type="invalid">{errors.description?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Summary Card */}
            {(watchProgram || watchBatchName) && (
              <Row className="mb-4">
                <Col>
                  <Card className="bg-light border-primary">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0 text-white">📋 Batch Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Program:</strong> {watchProgram?.label || 'Not selected'}
                          </p>
                          <p className="mb-1">
                            <strong>Teacher:</strong> {watchTeacher?.label || 'Not selected'}
                          </p>
                          <p className="mb-0"><strong>Batch Name:</strong> {watchBatchName || 'Not specified'}</p>
                        </Col>
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Duration:</strong> {watchStart && watchEnd ? `${watchStart} → ${watchEnd}` : 'Not set'}
                          </p>
                          <p className="mb-1"><strong>Time:</strong> {watchTime || 'Not set'}</p>
                          <p className="mb-1">
                            <strong>Days:</strong> {watchDays.length ? watchDays.map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label).join(', ') : 'None'}
                          </p>
                          <p className="mb-0"><strong>Capacity:</strong> {watchCapacity || 'Not set'}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-2 flex-wrap">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-1" /> {isEdit ? 'Update Batch' : 'Create Batch'}
                  </>
                )}
              </Button>

              <Button type="button" variant="outline-secondary" onClick={onReset} disabled={loading}>
                <i className="fas fa-sync-alt me-1" /> Reset
              </Button>

              <Button
                type="button"
                variant="outline-danger"
                onClick={() => {
                  if (window.confirm('Cancel? Unsaved changes will be lost.')) navigate('/institute-batch');
                }}
                disabled={loading}
              >
                <i className="fas fa-times me-1" /> Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default InstituteAddBatch;
